import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

function AdminPanel({ user, token }) {
  const [auctions, setAuctions] = useState([]);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSection, setActiveSection] = useState('auctions');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUsersPanel, setShowUsersPanel] = useState(false);
  const [newAuction, setNewAuction] = useState({
    card_id: '',
    starting_bid: '',
    duration_minutes: '10',
    start_time: ''
  });
  const [patapizzeForm, setPatapizzeForm] = useState({
    userId: '',
    amount: '',
    operation: 'add'
  });
  const [passwordForm, setPasswordForm] = useState({
    userId: '',
    password: '',
    confirmPassword: ''
  });
  const [registrationsEnabled, setRegistrationsEnabled] = useState(true);
  const [galleryEnabled, setGalleryEnabled] = useState(true);
  const [tournaments, setTournaments] = useState([]);
  const [showTournamentForm, setShowTournamentForm] = useState(false);
  const [tournamentForm, setTournamentForm] = useState({
    name: '',
    description: '',
    startDate: '',
    maxParticipants: '20'
  });
  const [announcementText, setAnnouncementText] = useState('');

  useEffect(() => {
    fetchData();
    fetchRegistrationStatus();
    fetchGalleryStatus();
    fetchTournaments();
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/announcement`)
        .then(res => setAnnouncementText(res.data.text))
        .catch(err => console.error(err));
  }, []);

  const fetchAvailableCards = async () => {
    const response = await axios.get(`${API_URL}/api/admin/cards/available`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setCards(response.data);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [auctionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/auctions/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetchAvailableCards()
      ]);
      
      setAuctions(auctionsRes.data);
    } catch (error) {
      setError('Errore nel caricamento dei dati');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = async () => {
    if (showCreateForm) {
      setShowCreateForm(false);
      return;
    }
    try {
      setError('');
      await fetchAvailableCards();
      setShowCreateForm(true);
    } catch (error) {
      setError('Errore nel caricamento delle carte disponibili');
      console.error('Error fetching available cards:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      setError('Errore nel caricamento degli utenti');
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateAuction = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/auctions`,
        {
          card_id: parseInt(newAuction.card_id),
          starting_bid: parseInt(newAuction.starting_bid),
          duration_minutes: parseInt(newAuction.duration_minutes, 10),
          no_time_limit: newAuction.duration_minutes === '0',
          start_time: newAuction.start_time || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add the new auction with complete card data to the state immediately
      const newAuctionWithCard = response.data;
      setAuctions(prevAuctions => [newAuctionWithCard, ...prevAuctions]);

      setSuccess('Asta creata con successo!');
      setNewAuction({
        card_id: '',
        starting_bid: '',
        duration_minutes: '10',
        start_time: ''
      });
      setShowCreateForm(false);
      fetchAvailableCards().catch(() => {});
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nella creazione dell\'asta');
    }
  };

  const startAuction = async (auctionId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auctions/${auctionId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the auction in state with complete card data
      const updatedAuction = response.data;
      setAuctions(prevAuctions => 
        prevAuctions.map(auction => 
          auction.id === auctionId ? updatedAuction : auction
        )
      );
      
      setSuccess('Asta avviata!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nell\'avviare l\'asta');
    }
  };

  const cancelAuction = async (auctionId) => {
    if (!window.confirm('Sei sicuro di voler cancellare questa asta?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/auctions/${auctionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('Asta cancellata!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nella cancellazione dell\'asta');
    }
  };

  const hardDeleteAuction = async (auctionId, auctionName) => {
    if (!window.confirm(`⚠️ ATTENZIONE: Stai per eliminare definitivamente l'asta "${auctionName}" dal database. Questa operazione NON è reversibile. Continuare?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/admin/auctions/${auctionId}/hard-delete`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('Asta eliminata definitivamente dal database!');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nell\'eliminazione definitiva dell\'asta');
    }
  };

  const stopAuction = async (auctionId) => {
    if (!window.confirm('Sei sicuro di voler fermare questa asta? Il vincitore riceverà la carta.')) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/auctions/${auctionId}/stop`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the auction in state with complete card data
      const updatedAuction = response.data.auction;
      setAuctions(prevAuctions => 
        prevAuctions.map(auction => 
          auction.id === auctionId ? updatedAuction : auction
        )
      );
      
      setSuccess('Asta fermata! Il vincitore ha ricevuto la carta.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nel fermare l\'asta');
    }
  };

  const fetchRegistrationStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings/registrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrationsEnabled(response.data.registrations_enabled);
    } catch (error) {
      console.error('Error fetching registration status:', error);
    }
  };

  const fetchGalleryStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/gallery`);
      setGalleryEnabled(response.data.gallery_enabled);
    } catch (error) {
      console.error('Error fetching gallery status:', error);
    }
  };

  const toggleGallery = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/settings/gallery`,
        { enabled: !galleryEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGalleryEnabled(response.data.gallery_enabled);
      setSuccess(response.data.message);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nel cambiamento delle impostazioni');
    }
  };

  const toggleRegistrations = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/settings/registrations`,
        { enabled: !registrationsEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRegistrationsEnabled(response.data.registrations_enabled);
      setSuccess(response.data.message);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nel cambiamento delle impostazioni');
    }
  };

  const handlePatapizze = async (e) => {
    e.preventDefault();
    if (!patapizzeForm.userId || !patapizzeForm.amount) {
      setError('Seleziona un utente e inserisci un importo');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/admin/users/${patapizzeForm.userId}/patapizze`,
        {
          amount: parseInt(patapizzeForm.amount),
          operation: patapizzeForm.operation
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(`Patapizze ${patapizzeForm.operation === 'add' ? 'aggiunte' : 'rimosse'} con successo!`);
      setPatapizzeForm({ userId: '', amount: '', operation: 'add' });
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nella gestione delle patapizze');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!passwordForm.userId || !passwordForm.password) {
      setError('Seleziona un utente e inserisci la nuova password');
      return;
    }
    if (passwordForm.password.length < 4) {
      setError('La password deve avere almeno 4 caratteri');
      return;
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/admin/users/${passwordForm.userId}/password`,
        { password: passwordForm.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const targetUser = users.find(u => String(u.id) === String(passwordForm.userId));
      setSuccess(`Password di ${targetUser?.username || 'utente'} aggiornata con successo!`);
      setPasswordForm({ userId: '', password: '', confirmPassword: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nell\'aggiornamento della password');
    }
  };

  const deleteUser = async (userId, username) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'utente ${username}? Tutte le sue carte saranno rese disponibili.`)) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(`Utente ${username} eliminato con successo!`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nell\'eliminazione dell\'utente');
    }
  };

  const restoreUser = async (userId, username) => {
    if (!window.confirm(`Sei sicuro di voler ripristinare l'utente ${username}?`)) {
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(`Utente ${username} ripristinato con successo!`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nel ripristino dell\'utente');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Senza limite';
    return new Date(dateString).toLocaleString('it-IT');
  };

  // Tournament functions
  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tournaments`);
      setTournaments(response.data);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  const handleCreateTournament = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/tournaments`, {
        name: tournamentForm.name,
        description: tournamentForm.description,
        start_date: tournamentForm.startDate,
        max_participants: parseInt(tournamentForm.maxParticipants)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Torneo creato con successo!');
      setTournamentForm({
        name: '',
        description: '',
        startDate: '',
        maxParticipants: '20'
      });
      setShowTournamentForm(false);
      fetchTournaments();
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nella creazione del torneo');
    }
  };

  const startTournament = async (tournamentId) => {
    try {
      await axios.post(`${API_URL}/api/tournaments/${tournamentId}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Torneo avviato con successo!');
      fetchTournaments();
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nell\'avvio del torneo');
    }
  };

  const endTournament = async (tournamentId) => {
    if (window.confirm('Sei sicuro di voler terminare questo torneo?')) {
      try {
        await axios.patch(`${API_URL}/api/tournaments/${tournamentId}`, 
          { status: 'ended' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setSuccess('Torneo terminato con successo!');
        fetchTournaments();
      } catch (error) {
        setError(error.response?.data?.error || 'Errore nella terminazione del torneo');
      }
    }
  };

  const hardDeleteTournament = async (tournamentId, tournamentName) => {
    if (!window.confirm(`⚠️ ATTENZIONE: Stai per eliminare definitivamente il torneo "${tournamentName}" dal database. Questa operazione eliminerà anche tutte le partite e i dati collegati. NON è reversibile. Continuare?`)) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/tournaments/${tournamentId}/hard-delete`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('Torneo eliminato definitivamente dal database!');
      fetchTournaments();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nell\'eliminazione definitiva del torneo');
    }
  };

  const syncUserCards = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/sync-user-cards`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Carte utenti sincronizzate con successo!');
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nella sincronizzazione delle carte');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'scheduled': { text: 'Programmata', class: 'badge-scheduled' },
      'active': { text: 'Attiva', class: 'badge-active' },
      'ended': { text: 'Terminata', class: 'badge-ended' },
      'cancelled': { text: 'Cancellata', class: 'badge-cancelled' }
    };
    
    const badge = badges[status] || { text: status, class: 'badge-default' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const getSectionIcon = (section) => {
    const icons = {
      'auctions': '🏛️',
      'tournaments': '🏟️', 
      'users': '👥',
      'settings': '⚙️'
    };
    return icons[section] || '📋';
  };

  const saveAnnouncement = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/settings/announcement`,
          { text: announcementText },
          { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Annuncio aggiornato!');
    } catch (err) {
      setError(err.response?.data?.error || 'Errore aggiornando annuncio');
    }
  };


  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Caricamento pannello admin...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>🛠️ Pannello Amministratore</h2>
        
        {/* Navigation Tabs */}
        <div className="admin-nav">
          {[
            { key: 'auctions', label: 'Aste' },
            { key: 'tournaments', label: 'Tornei' },
            { key: 'users', label: 'Utenti' },
            { key: 'settings', label: 'Impostazioni' }
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`nav-tab ${activeSection === key ? 'active' : ''}`}
              onClick={() => {
                setActiveSection(key);
                if (key === 'users') {
                  setShowUsersPanel(true);
                  fetchUsers();
                }
              }}
            >
              {getSectionIcon(key)} {label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Settings Section */}
      {activeSection === 'settings' && (
          <div className="admin-section">
            <div className="settings-panel">
              <h3>⚙️ Impostazioni Sistema</h3>

              {/* Toggle Registrations */}
              <div className="registration-toggle">
                <label className="toggle-label">
          <span className="toggle-text">
            🔐 Registrazioni: <strong>{registrationsEnabled ? 'Abilitate' : 'Disabilitate'}</strong>
          </span>
                  <input
                      type="checkbox"
                      checked={registrationsEnabled}
                      onChange={toggleRegistrations}
                      className="toggle-input"
                  />
                </label>
              </div>

              <div className="registration-toggle">
                <label className="toggle-label">
          <span className="toggle-text">
            📚 Raccolta carte: <strong>{galleryEnabled ? 'Visibile' : 'Nascosta'}</strong>
          </span>
                  <input
                      type="checkbox"
                      checked={galleryEnabled}
                      onChange={toggleGallery}
                      className="toggle-input"
                  />
                </label>
              </div>

              {/* Sync User Cards */}
              <button
                  className="btn btn-secondary"
                  onClick={syncUserCards}
              >
                🔄 Sincronizza Carte Utenti
              </button>

              {/* 🔹 Announcement Editor */}
              <div className="announcement-settings" style={{ marginTop: '20px' }}>
                <h4>📰 Annuncio in alto (Ticker)</h4>
                <textarea
                    value={announcementText}
                    onChange={(e) => setAnnouncementText(e.target.value)}
                    rows="2"
                    placeholder="Inserisci qui il messaggio che scorrerà in alto..."
                    className="form-control"
                />
                <button
                    className="btn btn-primary"
                    style={{ marginTop: '10px' }}
                    onClick={saveAnnouncement}
                >
                  💾 Salva Annuncio
                </button>
              </div>
            </div>
          </div>
      )}



      {/* Users Section */}
      {activeSection === 'users' && (
        <div className="admin-section">
          <h3>👥 Gestione Utenti</h3>
          
          <form onSubmit={handlePatapizze} className="patapizze-form">
            <h4>⚪ Gestione Patapizze</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Utente</label>
                <select 
                  value={patapizzeForm.userId}
                  onChange={(e) => setPatapizzeForm({...patapizzeForm, userId: e.target.value})}
                  required
                >
                  <option value="">Seleziona utente</option>
                  {users.filter(u => !u.is_deleted).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username} ({user.patapizze} patapizze)
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Importo</label>
                <input 
                  type="number"
                  value={patapizzeForm.amount}
                  onChange={(e) => setPatapizzeForm({...patapizzeForm, amount: e.target.value})}
                  min="1"
                  required
                />
              </div>
              <div className="form-group">
                <label>Operazione</label>
                <select 
                  value={patapizzeForm.operation}
                  onChange={(e) => setPatapizzeForm({...patapizzeForm, operation: e.target.value})}
                >
                  <option value="add">Aggiungi</option>
                  <option value="remove">Rimuovi</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary">
                {patapizzeForm.operation === 'add' ? '➕ Aggiungi' : '➖ Rimuovi'}
              </button>
            </div>
          </form>

          <form onSubmit={handleChangePassword} className="patapizze-form">
            <h4>🔑 Modifica Password</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Utente</label>
                <select
                  value={passwordForm.userId}
                  onChange={(e) => setPasswordForm({...passwordForm, userId: e.target.value})}
                  required
                >
                  <option value="">Seleziona utente</option>
                  {users.filter(u => !u.is_deleted).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.username}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Nuova password</label>
                <input
                  type="password"
                  value={passwordForm.password}
                  onChange={(e) => setPasswordForm({...passwordForm, password: e.target.value})}
                  minLength="4"
                  placeholder="Minimo 4 caratteri"
                  required
                />
              </div>
              <div className="form-group">
                <label>Conferma password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  minLength="4"
                  placeholder="Ripeti la password"
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary">
                💾 Salva password
              </button>
            </div>
          </form>

          <div className="users-table">
            <h4>📊 Lista Utenti</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Utente</th>
                    <th>Patapizze</th>
                    <th>Admin</th>
                    <th>Stato</th>
                    <th>Registrato</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} className={user.is_deleted ? 'deleted-user' : ''}>
                      <td>{user.username}</td>
                      <td><span className="patapizza-icon"></span> {user.patapizze}</td>
                      <td>{user.is_admin ? '👑' : '👤'}</td>
                      <td>
                        {user.is_deleted ? (
                          <span className="status-badge badge-deleted">🗑️ Eliminato</span>
                        ) : (
                          <span className="status-badge badge-active">✅ Attivo</span>
                        )}
                      </td>
                      <td>{formatDateTime(user.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          {!user.is_deleted && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setPasswordForm({ userId: String(user.id), password: '', confirmPassword: '' })}
                            >
                              🔑 Password
                            </button>
                          )}
                          {!user.is_deleted ? (
                            <button 
                              className="btn btn-danger btn-sm"
                              onClick={() => deleteUser(user.id, user.username)}
                              disabled={user.is_admin}
                            >
                              🗑️ Elimina
                            </button>
                          ) : (
                            <button 
                              className="btn btn-success btn-sm"
                              onClick={() => restoreUser(user.id, user.username)}
                            >
                              🔄 Ripristina
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Auctions Section */}
      {activeSection === 'auctions' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>🏛️ Gestione Aste</h3>
            <button 
              className="btn btn-primary"
              onClick={openCreateForm}
            >
              {showCreateForm ? 'Chiudi Form' : 'Crea Nuova Asta'}
            </button>
          </div>

          {showCreateForm && (
            <div className="create-auction-form">
              <h4>Crea Nuova Asta</h4>
              <form onSubmit={handleCreateAuction}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Carta</label>
                    <select
                      value={newAuction.card_id}
                      onChange={(e) => setNewAuction({...newAuction, card_id: e.target.value})}
                      required
                    >
                      <option value="">Seleziona una carta...</option>
                      {cards.length === 0 ? (
                        <option value="" disabled>Nessuna carta disponibile</option>
                      ) : (
                        cards.map(card => (
                          <option key={card.id} value={card.id}>
                            {card.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Offerta iniziale (patapizze)</label>
                    <input
                      type="number"
                      value={newAuction.starting_bid}
                      onChange={(e) => setNewAuction({...newAuction, starting_bid: e.target.value})}
                      min="1"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Durata (minuti)</label>
                    <select
                      value={newAuction.duration_minutes}
                      onChange={(e) => setNewAuction({...newAuction, duration_minutes: e.target.value})}
                    >
                      <option value="0">Senza limite (ferma a mano)</option>
                      <option value="5">5 minuti</option>
                      <option value="10">10 minuti</option>
                      <option value="15">15 minuti</option>
                      <option value="30">30 minuti</option>
                      <option value="60">1 ora</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Orario di inizio (opzionale - lascia vuoto per iniziare subito)</label>
                  <input
                    type="datetime-local"
                    value={newAuction.start_time}
                    onChange={(e) => setNewAuction({...newAuction, start_time: e.target.value})}
                  />
                </div>
                
                <button type="submit" className="btn btn-primary">
                  Crea Asta
                </button>
              </form>
            </div>
          )}

          <div className="auctions-table">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Carta</th>
                    <th>Stato</th>
                    <th>Offerta Iniziale</th>
                    <th>Offerta Attuale</th>
                    <th>Inizio</th>
                    <th>Fine</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {auctions.map(auction => (
                    <tr key={auction.id}>
                      <td>{auction.name}</td>
                      <td>{getStatusBadge(auction.status)}</td>
                      <td>
                        <span className="patapizza-icon"></span>
                        {auction.starting_bid.toLocaleString()}
                      </td>
                      <td>
                        <span className="patapizza-icon"></span>
                        {auction.current_bid.toLocaleString()}
                        {auction.highest_bidder_username && (
                          <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                            di {auction.highest_bidder_username}
                          </div>
                        )}
                      </td>
                      <td>{auction.start_time ? formatDateTime(auction.start_time) : '-'}</td>
                      <td>{formatDateTime(auction.end_time)}</td>
                      <td>
                        <div className="action-buttons">
                          {auction.status === 'scheduled' && (
                            <button 
                              className="btn btn-start"
                              onClick={() => startAuction(auction.id)}
                            >
                              Avvia
                            </button>
                          )}
                          {auction.status === 'active' && (
                            <button 
                              className="btn btn-stop"
                              onClick={() => stopAuction(auction.id)}
                            >
                              Ferma
                            </button>
                          )}
                          {['scheduled', 'active'].includes(auction.status) && (
                            <button 
                              className="btn btn-cancel"
                              onClick={() => cancelAuction(auction.id)}
                            >
                              Cancella
                            </button>
                          )}
                          <button 
                            className="btn btn-danger-hard"
                            onClick={() => hardDeleteAuction(auction.id, auction.name)}
                            title="Elimina definitivamente dal database (NON reversibile)"
                          >
                            🗑️ Hard Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tournaments Section */}
      {activeSection === 'tournaments' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>🏟️ Gestione Tornei</h3>
            <button 
              className="btn btn-primary"
              onClick={() => setShowTournamentForm(!showTournamentForm)}
            >
              {showTournamentForm ? 'Nascondi Form' : 'Nuovo Torneo'}
            </button>
          </div>

          {showTournamentForm && (
            <div className="tournament-form">
              <h4>Crea Nuovo Torneo</h4>
              <form onSubmit={handleCreateTournament}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nome Torneo</label>
                    <input 
                      type="text"
                      value={tournamentForm.name}
                      onChange={(e) => setTournamentForm({...tournamentForm, name: e.target.value})}
                      placeholder="Es: Fantavaro Championship 2024"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Partecipanti</label>
                    <input 
                      type="number"
                      value={tournamentForm.maxParticipants}
                      onChange={(e) => setTournamentForm({...tournamentForm, maxParticipants: e.target.value})}
                      min="2"
                      max="50"
                      placeholder="20"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Descrizione</label>
                  <textarea 
                    value={tournamentForm.description}
                    onChange={(e) => setTournamentForm({...tournamentForm, description: e.target.value})}
                    placeholder="Descrizione del torneo..."
                    rows="3"
                  />
                </div>
                
                <div className="form-group">
                  <label>Data Inizio</label>
                  <input 
                    type="datetime-local"
                    value={tournamentForm.startDate}
                    onChange={(e) => setTournamentForm({...tournamentForm, startDate: e.target.value})}
                    required
                  />
                </div>

                <button type="submit" className="btn btn-primary">
                  🏟️ Crea Torneo
                </button>
              </form>
            </div>
          )}

          <div className="tournaments-list">
            <h4>Tornei Esistenti</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Partecipanti</th>
                    <th>Data Inizio</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {tournaments.map(tournament => (
                    <tr key={tournament.id}>
                      <td><strong>{tournament.name}</strong></td>
                      <td>
                        <span className={`status-badge badge-${tournament.status}`}>
                          {tournament.status === 'scheduled' ? 'Programmato' :
                           tournament.status === 'active' ? 'Attivo' :
                           tournament.status === 'ended' ? 'Terminato' : tournament.status}
                        </span>
                      </td>
                      <td>{tournament.participant_count || 0} / {tournament.max_participants || '∞'}</td>
                      <td>{formatDateTime(tournament.start_date)}</td>
                      <td>
                        <div className="action-buttons">
                          {tournament.status === 'scheduled' && (
                            <button 
                              className="btn btn-start"
                              onClick={() => startTournament(tournament.id)}
                            >
                              Avvia
                            </button>
                          )}
                          {tournament.status === 'active' && (
                            <button 
                              className="btn btn-stop"
                              onClick={() => endTournament(tournament.id)}
                            >
                              Termina
                            </button>
                          )}
                          <button 
                            className="btn btn-danger-hard"
                            onClick={() => hardDeleteTournament(tournament.id, tournament.name)}
                            title="Elimina definitivamente dal database (NON reversibile)"
                          >
                            🗑️ Hard Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;